/**
 * The `/Auth/me` snapshot behind TanStack Query (HS-1387, C2).
 *
 * The session itself stays where it was — in the `AuthClient`'s reducer, read through
 * `useAuth()`. This module owns only the *schedule*: when the snapshot is re-read. Query is
 * what makes that schedule declarative (refetch on focus, on reconnect, on an explicit
 * invalidation) instead of a hand-rolled listener, which is why the package now carries
 * `@tanstack/react-query` as a peer alongside `react`. Both apps already bundle it, so the
 * dependency costs no bytes.
 *
 * The query's `data` is incidental — `refreshSession()` has already folded the result into the
 * machine by the time it resolves, and every consumer reads the session from `useAuth()`. What
 * matters is that Query owns the timer.
 */

import { useQuery, type QueryClient } from '@tanstack/react-query';

import type { Session } from './machine';
import { useAuth } from './useAuth';

/**
 * The session snapshot's cache key.
 *
 * It is the endpoint path, matching how `@smartsash/server-ops` keys everything else — but note
 * that it deliberately does NOT sit under any `entityRoot` prefix that package cascades over
 * (`permission` is rooted at `/api/institutions`). A mutation that changes permissions therefore
 * has to invalidate this key by name; `invalidateSession` below is that call.
 */
export const SESSION_QUERY_KEY = ['/api/Auth/me'] as const;

/**
 * How long a freshly-read snapshot is trusted for everything EXCEPT a window focus.
 *
 * It governs mount and reconnect, so a remount inside the window does not re-read. Thirty seconds
 * matches portal's default for every other query.
 */
const SESSION_STALE_TIME_MS = 30_000;

/** One retry, matching portal's default: enough for a flaky hop, not enough to hammer. */
const SESSION_RETRY_COUNT = 1;

/**
 * Keep the signed-in session's permission snapshot fresh.
 *
 * Mount once, inside both `AuthProvider` and `QueryClientProvider`. It renders nothing.
 *
 * Disabled while signed out: there is no snapshot to refresh, and probing `/Auth/me` on the
 * login screen would fire a 401 on every window focus.
 */
export function useSessionRefresh(): void {
  const { state, auth } = useAuth();

  useQuery<Session | null>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => auth.refreshSession(),
    enabled: state.status === 'authenticated',
    staleTime: SESSION_STALE_TIME_MS,
    /**
     * `'always'`, not the default `true` — and this is the setting the whole feature rests on.
     *
     * The default respects `staleTime`, so with a 30-second window a permission revoked while
     * the user was away would stay invisible for up to 30 seconds after they came back, showing
     * controls the server has already stopped honouring. That is exactly the failure this ticket
     * exists to end, only shorter. An e2e caught it: the sidebar still offered the revoked
     * destination after a focus, because the snapshot was two seconds old.
     *
     * The cost is one small GET per window focus, which is the cheapest possible price for a UI
     * that does not lie about what its user may do.
     */
    refetchOnWindowFocus: 'always',
    retry: SESSION_RETRY_COUNT,
  });
}

/**
 * Re-read the permission snapshot now.
 *
 * Call this from any mutation that can change what the CURRENT user may do — granting or
 * revoking a permission, above all when the target is the caller themselves. Without it the
 * person who just edited their own permissions is the last to see the result.
 */
export function invalidateSession(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
}
